import MobileMenu, { MobileMenuCSS } from "./MobileMenu";
import { useState, useEffect, useCallback } from "react";
import { supabase } from "./supabaseClient";
import { useToast, ToastContainer, BillListSkeleton, ConfirmDialog, BillStatusBadge } from "./toast";

/* ─────────────────────────────────────────────────────────
   LEGISCAN BILL TRACKER
   Data source: LegiScan API (https://legiscan.com)
   Attribution: Data provided by LegiScan

   Query strategy (per LegiScan crash course):
   1. getMasterListRaw  — fetched once, cached 24h in localStorage
      Returns all MI bills with change_hash values
   2. getSearchRaw      — called on keyword search, cached 15 min
   3. getBill           — called on bill click, only if change_hash
      differs from cached value; result cached indefinitely until hash changes

   This keeps query spend well within 30,000/month.
───────────────────────────────────────────────────────── */

const PROXY = process.env.REACT_APP_PROXY_URL || "https://legisim-server.onrender.com";

const CACHE_KEY_MASTERLIST   = "wc_db_masterlist_v4";
const CACHE_KEY_MASTERLIST_TS = "wc_db_masterlist_ts_v4";
const CACHE_KEY_BILLS        = "wc_db_bills_v4"; // JSON object keyed by bill_id
const MASTERLIST_TTL_MS      = 60 * 60 * 1000; // 1 hour

// ─── CACHE HELPERS ────────────────────────────────────────
function getMasterListCache() {
  try {
    const ts = parseInt(localStorage.getItem(CACHE_KEY_MASTERLIST_TS) || "0");
    if (Date.now() - ts > MASTERLIST_TTL_MS) return null; // stale
    const raw = localStorage.getItem(CACHE_KEY_MASTERLIST);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function setMasterListCache(data) {
  try {
    localStorage.setItem(CACHE_KEY_MASTERLIST, JSON.stringify(data));
    localStorage.setItem(CACHE_KEY_MASTERLIST_TS, String(Date.now()));
  } catch {}
}

function getBillCache(bill_id) {
  try {
    const all = JSON.parse(localStorage.getItem(CACHE_KEY_BILLS) || "{}");
    return all[bill_id] || null;
  } catch { return null; }
}

function setBillCache(bill_id, bill, change_hash) {
  try {
    const all = JSON.parse(localStorage.getItem(CACHE_KEY_BILLS) || "{}");
    all[bill_id] = { bill, change_hash, cached_at: Date.now() };
    localStorage.setItem(CACHE_KEY_BILLS, JSON.stringify(all));
  } catch {}
}

function getBillCacheHash(bill_id) {
  try {
    const all = JSON.parse(localStorage.getItem(CACHE_KEY_BILLS) || "{}");
    return all[bill_id]?.change_hash || null;
  } catch { return null; }
}

// ─── SUPABASE WATCHLIST HELPERS ───────────────────────────
async function getWatchlist(userId) {
  if (!userId) return [];
  try {
    const { data, error } = await supabase
      .from("tracked_bills")
      .select("*")
      .eq("user_id", userId)
      .order("tracked_at", { ascending: false });
    if (error) throw error;
    return data || [];
  } catch { return []; }
}

async function addToWatchlist(userId, bill) {
  if (!userId) return false;
  try {
    const { error } = await supabase.from("tracked_bills").upsert({
      user_id: userId,
      bill_id: bill.bill_id,
      bill_number: bill.bill_number,
      title: bill.title,
      status: bill.status,
      last_action: bill.last_action,
      last_action_date: bill.last_action_date,
      change_hash: bill.change_hash,
      tracked_at: new Date().toISOString(),
      last_seen_at: new Date().toISOString(),
    }, { onConflict: "user_id,bill_id" });
    return !error;
  } catch { return false; }
}

async function removeFromWatchlist(userId, bill_id) {
  if (!userId) return;
  try {
    await supabase
      .from("tracked_bills")
      .delete()
      .eq("user_id", userId)
      .eq("bill_id", bill_id);
  } catch {}
}

// ─── API CALLS ────────────────────────────────────────────
async function fetchMasterList() {
  const res = await fetch(`${PROXY}/api/db/masterlist`);
  if (!res.ok) throw new Error(`Server error: ${res.status}`);
  const data = await res.json();
  if (data.error) throw new Error(data.error);
  return data.bills; // array from DB with description, last_action_date
}

async function fetchBill(bill_id) {
  const res = await fetch(`${PROXY}/api/db/bill?id=${bill_id}`);
  if (!res.ok) throw new Error(`Server error: ${res.status}`);
  const data = await res.json();
  if (data.error) throw new Error(data.error);
  return data.bill;
}

async function fetchSearch(query, page = 1) {
  const res = await fetch(`${PROXY}/api/db/search?query=${encodeURIComponent(query)}`);
  if (!res.ok) throw new Error(`Server error: ${res.status}`);
  const data = await res.json();
  if (data.error) throw new Error(data.error);
  return data.bills || data.results || [];
}

// ─── HELPERS ──────────────────────────────────────────────
const STATUS_MAP = {
  1: "Introduced", 2: "Engrossed", 3: "Enrolled", 4: "Passed",
  5: "Vetoed", 6: "Failed", 7: "Override", 8: "Chaptered",
  9: "Refer", 10: "Report Pass", 11: "Report Fail", 12: "Draft",
};

function statusLabel(code) {
  return displayStatus(STATUS_MAP[code] || "Introduced");
}

const STATUS_COLORS = {
  "Passed":     { bg:"rgba(59,163,96,0.1)",  border:"rgba(59,163,96,0.25)",  text:"#3ba360" },
  "Enrolled":   { bg:"rgba(59,163,96,0.1)",  border:"rgba(59,163,96,0.25)",  text:"#3ba360" },
  "Chaptered":  { bg:"rgba(59,163,96,0.1)",  border:"rgba(59,163,96,0.25)",  text:"#3ba360" },
  "PA Signed":  { bg:"rgba(59,163,96,0.1)",  border:"rgba(59,163,96,0.25)",  text:"#3ba360" },
  "In Committee":{ bg:"rgba(201,168,76,0.1)",border:"rgba(201,168,76,0.25)", text:"var(--gold)" },
  "Engrossed":  { bg:"rgba(201,168,76,0.1)", border:"rgba(201,168,76,0.25)", text:"var(--gold)" },
  "Report Pass":{ bg:"rgba(201,168,76,0.1)", border:"rgba(201,168,76,0.25)", text:"var(--gold)" },
  "Introduced": { bg:"rgba(100,100,200,0.1)",border:"rgba(100,100,200,0.25)",text:"#8888dd" },
  "Draft":      { bg:"rgba(100,100,200,0.1)",border:"rgba(100,100,200,0.25)",text:"#8888dd" },
  "Failed":     { bg:"rgba(212,59,59,0.1)",  border:"rgba(212,59,59,0.25)",  text:"var(--rep)" },
  "Vetoed":     { bg:"rgba(212,59,59,0.1)",  border:"rgba(212,59,59,0.25)",  text:"var(--rep)" },
  "Report Fail":{ bg:"rgba(212,59,59,0.1)",  border:"rgba(212,59,59,0.25)",  text:"var(--rep)" },
};

function statusStyle(s) {
  return STATUS_COLORS[s] || STATUS_COLORS["Introduced"];
}

function masterlistToItem(entry) {
  // Always normalize status to string — LegiScan sometimes returns numeric codes
  const statusRaw = entry.status_text && entry.status_text !== "null" ? entry.status_text : null;
  const status = displayStatus(statusRaw || statusLabel(typeof entry.status === "number" ? entry.status : parseInt(entry.status) || 1));
  const num = entry.bill_number || entry.number || "";
  const actionDate = entry.last_action_date || entry.status_date || null;
  return {
    bill_id:          entry.bill_id,
    bill_number:      num,
    title:            entry.title,
    description:      entry.description || null,
    status,
    status_date:      actionDate,
    last_action:      entry.last_action || null,
    last_action_date: actionDate,
    change_hash:      entry.change_hash,
    chamber:          num.startsWith("H") ? "House" : "Senate",
  };
}

/* ─── STYLES ─────────────────────────────────────────────── */

function displayStatus(s) {
  const MAP = {
    "Introduced":"Introduced","Engrossed":"In Progress","Enrolled":"PA Signed",
    "Chaptered":"PA Signed","Passed":"Passed","Failed":"Failed","Vetoed":"Vetoed",
    "Report Pass":"In Committee","Report Fail":"In Committee","Refer":"In Committee",
    "Override":"Passed","Draft":"Draft",
  };
  return MAP[s] || s || "Introduced";
}
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,800;0,900;1,700&family=IBM+Plex+Sans:wght@300;400;500&family=IBM+Plex+Mono:wght@400;500&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
/* Theme variables defined globally in public/theme.css */
body{background:var(--bg);color:var(--text);font-family:var(--font-body);-webkit-font-smoothing:antialiased;}
.bt-app{display:flex;flex-direction:column;height:100vh;overflow:hidden;background:var(--bg);}
.legisim-masthead{flex-shrink:0;background:var(--surface);border-bottom:2px solid rgba(201,168,76,0.22);padding:0 1.25rem;display:flex;align-items:center;justify-content:space-between;height:58px;position:relative;z-index:20;}
.legisim-masthead::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;background:linear-gradient(90deg,transparent 0%,var(--gold) 30%,#e8c96a 50%,var(--gold) 70%,transparent 100%);}
.legisim-masthead::after{content:'';position:absolute;inset:0;background-image:linear-gradient(rgba(201,168,76,0.025) 1px,transparent 1px),linear-gradient(90deg,rgba(201,168,76,0.025) 1px,transparent 1px);background-size:44px 44px;pointer-events:none;}
.masthead-left{display:flex;align-items:center;gap:1rem;z-index:1;}
.masthead-right{display:flex;align-items:center;gap:0.6rem;z-index:1;}
.mast-back{background:transparent;border:2px solid var(--gold-dim);border-radius:8px;padding:0.45rem 1.1rem;color:var(--gold);font-family:var(--font-mono);font-size:0.72rem;font-weight:600;cursor:pointer;letter-spacing:0.07em;transition:all 0.18s;white-space:nowrap;}
.mast-back:hover{border-color:var(--gold);background:var(--gold-glow);}
.mast-btn-sm{background:transparent;border:1px solid var(--border);border-radius:8px;padding:0.4rem 1rem;color:var(--muted);font-family:var(--font-mono);font-size:0.7rem;cursor:pointer;letter-spacing:0.06em;transition:all 0.18s;white-space:nowrap;}
.mast-btn-sm:hover{border-color:var(--gold-dim);color:var(--gold);}
.mast-btn-sm.signout:hover{border-color:rgba(192,57,43,0.5);color:var(--red);}
.masthead-wordmark{font-family:var(--font-display);font-size:1.35rem;font-weight:800;letter-spacing:-0.02em;color:var(--text);}
.masthead-wordmark em{font-style:italic;color:var(--gold);}
.masthead-wordmark sup{font-family:var(--font-mono);font-size:0.42rem;color:var(--muted);vertical-align:super;}
.masthead-divider{width:1px;height:20px;background:var(--border);}
.masthead-tagline{font-family:var(--font-mono);font-size:0.58rem;color:var(--muted);letter-spacing:0.12em;text-transform:uppercase;}
.masthead-badge{display:inline-flex;align-items:center;gap:0.3rem;background:rgba(201,168,76,0.08);border:1px solid rgba(201,168,76,0.2);border-radius:100px;padding:0.18rem 0.6rem;font-family:var(--font-mono);font-size:0.54rem;color:var(--gold);letter-spacing:0.1em;text-transform:uppercase;}
.masthead-badge-dot{width:5px;height:5px;border-radius:50%;background:var(--gold);animation:mbpulse 2.5s infinite;}
@keyframes mbpulse{0%,100%{opacity:1}50%{opacity:0.4}}
.masthead-nav{display:flex;align-items:center;gap:0.3rem;z-index:1;flex-shrink:1;overflow:hidden;}
.mast-btn{background:transparent;border:1px solid var(--border);border-radius:6px;padding:0.36rem 0.7rem;color:var(--muted);font-family:var(--font-mono);font-size:0.64rem;cursor:pointer;letter-spacing:0.07em;transition:all 0.18s;white-space:nowrap;}
.mast-btn:hover{border-color:var(--gold-dim);color:var(--gold);background:rgba(201,168,76,0.05);}
.mast-btn.gold{border-color:var(--gold-dim);color:var(--gold);}
.mast-btn.gold:hover{background:rgba(201,168,76,0.1);}
.mast-btn.active{border-color:var(--gold);color:var(--gold);background:rgba(201,168,76,0.1);}
.mast-sep{width:1px;height:18px;background:var(--border);margin:0 0.2rem;}
.bt-body{display:flex;flex:1;overflow:hidden;}
.bt-left{width:320px;flex-shrink:0;background:var(--surface);border-right:1px solid var(--border);display:flex;flex-direction:column;overflow:hidden;}
.bt-left-header{padding:1rem 1.25rem;border-bottom:1px solid var(--border);flex-shrink:0;}
.bt-left-title{font-family:var(--font-mono);font-size:0.62rem;color:var(--muted);letter-spacing:0.12em;text-transform:uppercase;margin-bottom:0.75rem;}
.search-wrap{position:relative;margin-bottom:0.6rem;}
.search-icon{position:absolute;left:0.65rem;top:50%;transform:translateY(-50%);width:14px;height:14px;stroke:var(--muted);fill:none;stroke-width:2;stroke-linecap:round;pointer-events:none;}
.search-input{width:100%;background:var(--surface2);border:1px solid var(--border);border-radius:6px;padding:0.55rem 0.85rem 0.55rem 2rem;color:var(--text);font-family:var(--font-body);font-size:0.83rem;outline:none;transition:border-color 0.2s;}
.search-input:focus{border-color:var(--gold-dim);}
.search-input::placeholder{color:var(--muted);}
.bt-filters{display:flex;flex-wrap:wrap;gap:0.35rem;}
.bt-filter-btn{background:var(--surface2);border:1px solid var(--border);border-radius:100px;padding:0.22rem 0.65rem;font-size:0.62rem;font-family:var(--font-mono);color:var(--muted);cursor:pointer;transition:all 0.15s;letter-spacing:0.05em;}
.bt-filter-btn:hover{border-color:var(--gold-dim);color:var(--text);}
.bt-filter-btn.active{background:var(--gold-glow);border-color:var(--gold-dim);color:var(--gold);}
.bt-list-meta{padding:0.5rem 1.25rem;font-family:var(--font-mono);font-size:0.62rem;color:var(--muted);letter-spacing:0.08em;border-bottom:1px solid var(--border);flex-shrink:0;display:flex;align-items:center;justify-content:space-between;}
.watchlist-btn-small{background:var(--gold-glow);border:1px solid var(--gold-dim);border-radius:6px;padding:0.2rem 0.6rem;color:var(--gold);font-family:var(--font-mono);font-size:0.58rem;cursor:pointer;letter-spacing:0.06em;transition:all 0.18s;}
.watchlist-btn-small:hover{background:rgba(201,168,76,0.2);}
.bt-bill-list{flex:1;overflow-y:auto;scrollbar-width:thin;scrollbar-color:var(--border) transparent;}
.bt-bill-item{padding:0.85rem 1.25rem;border-bottom:1px solid var(--border);cursor:pointer;transition:background 0.15s;}
.bt-bill-item:hover{background:var(--surface2);}
.bt-bill-item.selected{background:var(--charcoal);border-left:2px solid var(--gold);}
.bt-bill-number{font-family:var(--font-mono);font-size:0.65rem;color:var(--gold);margin-bottom:0.2rem;}
.bt-bill-title{font-size:0.8rem;color:var(--text);line-height:1.35;margin-bottom:0.35rem;}
.bt-bill-meta{display:flex;align-items:center;gap:0.5rem;flex-wrap:wrap;}
.status-badge{font-family:var(--font-mono);font-size:0.58rem;padding:0.15rem 0.45rem;border-radius:4px;font-weight:500;}
.bt-loading{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:1rem;}
.bt-loading-ring{width:36px;height:36px;border:2px solid var(--border);border-top-color:var(--gold);border-radius:50%;animation:spin 1s linear infinite;}
@keyframes spin{to{transform:rotate(360deg)}}
.bt-loading-text{font-family:var(--font-mono);font-size:0.68rem;color:var(--muted);letter-spacing:0.08em;}
.bt-error{padding:1.25rem;font-family:var(--font-mono);font-size:0.7rem;color:#d43b3b;line-height:1.6;}
.bt-main{flex:1;display:flex;flex-direction:column;overflow:hidden;}
.bt-empty{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:0.75rem;}
.bt-empty-icon{font-size:2.5rem;opacity:0.2;}
.bt-empty-text{font-family:var(--font-mono);font-size:0.68rem;color:var(--muted);text-align:center;line-height:1.7;letter-spacing:0.04em;}
.bt-detail{flex:1;overflow-y:auto;scrollbar-width:thin;scrollbar-color:var(--border) transparent;}
.bt-detail-loading{flex:1;display:flex;align-items:center;justify-content:center;gap:0.75rem;font-family:var(--font-mono);font-size:0.68rem;color:var(--muted);}
.bt-detail-header{padding:1.5rem;border-bottom:1px solid var(--border);position:sticky;top:0;background:var(--surface);z-index:5;}
.bt-detail-number{font-family:var(--font-mono);font-size:0.62rem;color:var(--gold);letter-spacing:0.12em;margin-bottom:0.3rem;}
.bt-detail-title{font-family:var(--font-display);font-size:1.3rem;font-weight:700;color:var(--text);line-height:1.25;margin-bottom:0.75rem;}
.bt-detail-actions{display:flex;align-items:center;gap:0.75rem;flex-wrap:wrap;}
.btn-assign-client{border-radius:6px;padding:0.38rem 1rem;font-family:var(--font-mono);font-size:0.65rem;cursor:pointer;letter-spacing:0.07em;transition:all 0.18s;border:1px solid var(--border);background:transparent;color:var(--muted);white-space:nowrap;}
.btn-assign-client:hover{border-color:var(--gold-dim);color:var(--gold);background:var(--gold-glow);}
.bt-assign-overlay{position:fixed;inset:0;background:rgba(0,0,0,0.65);backdrop-filter:blur(4px);z-index:500;display:flex;align-items:center;justify-content:center;padding:1.5rem;}
.bt-assign-modal{background:var(--surface);border:1px solid var(--border);border-radius:14px;width:100%;max-width:460px;overflow:hidden;display:flex;flex-direction:column;}
.bt-assign-head{padding:1.1rem 1.4rem;border-bottom:1px solid var(--border);display:flex;align-items:flex-start;justify-content:space-between;gap:1rem;}
.bt-assign-title{font-size:0.85rem;font-weight:700;color:var(--text);}
.bt-assign-sub{font-family:var(--font-mono);font-size:0.6rem;color:var(--muted);margin-top:3px;}
.bt-assign-body{padding:1rem 1.2rem;overflow-y:auto;max-height:320px;display:flex;flex-direction:column;gap:0.45rem;}
.bt-assign-row{display:flex;align-items:center;gap:0.75rem;padding:0.55rem 0.75rem;border:1px solid var(--border);border-radius:8px;cursor:pointer;transition:all 0.15s;}
.bt-assign-row:hover{border-color:var(--gold-dim);background:var(--gold-glow);}
.bt-assign-row.sel{border-color:var(--gold);background:rgba(201,168,76,0.08);}
.bt-assign-check{width:15px;height:15px;border-radius:4px;border:1px solid var(--border);flex-shrink:0;display:flex;align-items:center;justify-content:center;font-size:0.6rem;color:transparent;}
.bt-assign-row.sel .bt-assign-check{background:var(--gold);border-color:var(--gold);color:#09090e;}
.bt-assign-name{font-size:0.8rem;color:var(--text);font-weight:500;}
.bt-assign-org{font-family:var(--font-mono);font-size:0.58rem;color:var(--muted);}
.bt-assign-empty{font-family:var(--font-mono);font-size:0.7rem;color:var(--muted);text-align:center;padding:1.25rem 0;}
.bt-assign-foot{padding:0.85rem 1.2rem;border-top:1px solid var(--border);display:flex;gap:0.5rem;justify-content:flex-end;}
.bt-assign-cancel{background:transparent;border:1px solid var(--border);border-radius:8px;padding:0.42rem 0.9rem;font-family:var(--font-mono);font-size:0.65rem;color:var(--muted);cursor:pointer;transition:all 0.15s;}
.bt-assign-cancel:hover{border-color:var(--gold-dim);color:var(--gold);}
.bt-assign-save{background:var(--gold);border:none;border-radius:8px;padding:0.42rem 1rem;font-family:var(--font-mono);font-size:0.65rem;font-weight:700;color:#09090e;cursor:pointer;transition:opacity 0.15s;}
.bt-assign-save:disabled{opacity:0.38;cursor:not-allowed;}
.bt-assign-save:not(:disabled):hover{opacity:0.85;}
.watch-btn{border-radius:6px;padding:0.38rem 1rem;font-family:var(--font-mono);font-size:0.65rem;cursor:pointer;letter-spacing:0.07em;transition:all 0.18s;border:1px solid var(--gold-dim);background:var(--gold-glow);color:var(--gold);}
.watch-btn:hover{background:rgba(201,168,76,0.2);}
.watch-btn.watching{background:rgba(59,163,96,0.1);border-color:rgba(59,163,96,0.3);color:#3ba360;}
.watch-btn.watching:hover{background:rgba(212,59,59,0.1);border-color:rgba(212,59,59,0.3);color:var(--rep);}
.section-label{font-family:var(--font-mono);font-size:0.58rem;color:var(--gold);letter-spacing:0.12em;text-transform:uppercase;padding:1rem 1.5rem 0.4rem;border-top:1px solid var(--border);}
.bt-description{padding:0.5rem 1.5rem 1rem;font-size:0.85rem;color:var(--muted);line-height:1.7;font-weight:300;}
.bt-info-grid{display:grid;grid-template-columns:1fr 1fr;gap:0.5rem;padding:0.5rem 1.5rem 0.75rem;}
.bt-info-card{background:var(--surface2);border:1px solid var(--border);border-radius:var(--r);padding:0.75rem;}
.bt-info-label{font-family:var(--font-mono);font-size:0.56rem;color:var(--muted);letter-spacing:0.08em;text-transform:uppercase;margin-bottom:0.25rem;}
.bt-info-value{font-size:0.82rem;color:var(--text);font-weight:500;line-height:1.3;}
.subject-tags{display:flex;flex-wrap:wrap;gap:0.35rem;padding:0.5rem 1.5rem 1rem;}
.subject-tag{background:var(--charcoal);border:1px solid var(--border);border-radius:4px;padding:0.2rem 0.5rem;font-size:0.68rem;color:var(--muted);}
.sponsor-list{padding:0.25rem 1.5rem 1rem;}
.sponsor-item{font-size:0.8rem;color:var(--text);padding:0.3rem 0;border-bottom:1px solid var(--border);}
.sponsor-item:last-child{border-bottom:none;}
.vote-row{display:flex;gap:0.75rem;padding:0.5rem 1.5rem 1rem;}
.vote-chip{display:flex;flex-direction:column;align-items:center;background:var(--surface2);border:1px solid var(--border);border-radius:var(--r);padding:0.6rem 1rem;min-width:60px;}
.vote-chip-val{font-family:var(--font-display);font-size:1.2rem;font-weight:700;}
.vote-chip-val.yea{color:#3ba360;}
.vote-chip-val.nay{color:var(--rep);}
.vote-chip-val.absent{color:var(--muted);}
.vote-chip-label{font-family:var(--font-mono);font-size:0.55rem;color:var(--muted);letter-spacing:0.08em;text-transform:uppercase;margin-top:0.2rem;}
.bt-source{font-family:var(--font-mono);font-size:0.54rem;color:var(--muted);padding:0.5rem 1.5rem 1.5rem;opacity:0.55;}
@media(max-width:1200px){
  .mast-btn{padding:0.3rem 0.45rem;font-size:0.58rem;letter-spacing:0.03em;}
  .masthead-nav{gap:0.2rem;}
}
@media(max-width:900px){
  .mast-btn{padding:0.28rem 0.38rem;font-size:0.55rem;}
  .masthead-nav{gap:0.15rem;}
}
@media(max-width:768px){
  .masthead-nav{display:none !important;}
  .legisim-masthead{flex-shrink:0;background:var(--surface);border-bottom:2px solid rgba(201,168,76,0.22);padding:0 1.25rem;display:flex;align-items:center;justify-content:space-between;height:58px;position:relative;z-index:20;}
}
@media(max-width:900px){.bt-left{width:240px;}}
@media(max-width:600px){.bt-left{display:none;}}

/* ── LIGHT MODE OVERRIDES ── */
[data-theme="light"] body{background:#eef1f7;color:#1a1e2e;}
[data-theme="light"] .page,[data-theme="light"] .app,[data-theme="light"] .bt-app,[data-theme="light"] .tl-app,[data-theme="light"] .map-app,[data-theme="light"] .cf-app{background:#eef1f7;}
[data-theme="light"] .legisim-masthead{background:#1a2744;border-bottom-color:rgba(255,255,255,0.1);}
[data-theme="light"] .legisim-masthead::before{background:linear-gradient(90deg,transparent,var(--gold),transparent);}
[data-theme="light"] .masthead-wordmark{color:#e6e4dc;}
[data-theme="light"] .masthead-wordmark em{color:#c9a84c;}
[data-theme="light"] .masthead-wordmark sup{color:rgba(230,228,220,0.5);}
[data-theme="light"] .mast-back{border-color:rgba(201,168,76,0.6);color:#c9a84c;background:transparent;}
[data-theme="light"] .mast-back:hover{background:rgba(201,168,76,0.12);border-color:#c9a84c;color:#c9a84c;}
[data-theme="light"] .mast-btn-sm{color:#e6e4dc;border-color:rgba(201,168,76,0.4);}
[data-theme="light"] .mast-btn-sm:hover{color:#ffffff;border-color:#c9a84c;background:rgba(201,168,76,0.1);}
[data-theme="light"] .mast-btn-sm.signout:hover{border-color:rgba(192,57,43,0.7);color:#ff6b6b;background:rgba(192,57,43,0.1);}
[data-theme="light"] .sidebar,[data-theme="light"] .bt-left,[data-theme="light"] .cal-sidebar,[data-theme="light"] .cf-sidebar,[data-theme="light"] .map-left{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .main,[data-theme="light"] .bt-detail,[data-theme="light"] .cf-main{background:#eef1f7;}
[data-theme="light"] .filter-btn,[data-theme="light"] .vh-filter-btn,[data-theme="light"] .bt-filter-btn{background:#f8f9fc;border-color:rgba(26,39,68,0.12);color:#6b7280;}
[data-theme="light"] .filter-btn.active,[data-theme="light"] .vh-filter-btn.active,[data-theme="light"] .bt-filter-btn.active{background:rgba(26,39,68,0.1);border-color:rgba(26,39,68,0.3);color:#1a2744;}
[data-theme="light"] .filter-btn.dem-active{background:var(--dem-bg);border-color:var(--dem);color:var(--dem);}
[data-theme="light"] .filter-btn.rep-active{background:var(--rep-bg);border-color:var(--rep);color:var(--rep);}
[data-theme="light"] .search-input,[data-theme="light"] .chat-input,[data-theme="light"] .bt-search-input,[data-theme="light"] .tl-search-input{background:#f8f9fc;border-color:rgba(26,39,68,0.12);color:#1a1e2e;}
[data-theme="light"] .leg-item.selected{background:#e8ecf4;}
[data-theme="light"] .leg-item.selected::before{background:#1a2744;}
[data-theme="light"] .leg-item:hover{background:#e8ecf4;}
[data-theme="light"] .chat-input-area{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .msg-bubble{background:#f0f2f8;border-color:rgba(26,39,68,0.1);}
[data-theme="light"] .msg.user .msg-bubble{background:#e8ecf4;border-color:rgba(26,39,68,0.2);}
[data-theme="light"] .send-btn{background:#1a2744;color:#f8f9fc;}
[data-theme="light"] .main-topbar,[data-theme="light"] .news-topbar,[data-theme="light"] .chamber-bar,[data-theme="light"] .toggle-bar{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .vh-header,[data-theme="light"] .vh-filters,[data-theme="light"] .vh-stats{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .vh-bill-number,[data-theme="light"] .bt-bill-number,[data-theme="light"] .bt-detail-number,[data-theme="light"] .tl-bill-number,[data-theme="light"] .district-num{color:#1a2744;}
[data-theme="light"] .profile-section-label,[data-theme="light"] .section-label,[data-theme="light"] .form-label,[data-theme="light"] .panel-title,[data-theme="light"] .output-label,[data-theme="light"] .floor-title,[data-theme="light"] .detail-title,[data-theme="light"] .search-section-label{color:#1a2744;}
[data-theme="light"] .input-panel{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .output-panel,[data-theme="light"] .output-body{background:#eef1f7;}
[data-theme="light"] .tl-add-btn,[data-theme="light"] .tl-refresh-btn,[data-theme="light"] .tl-view-btn{color:#1a2744;border-color:rgba(26,39,68,0.2);}
[data-theme="light"] .tl-table-head{background:#e8ecf4;}
[data-theme="light"] .tl-tr:hover{background:#e8ecf4;}
[data-theme="light"] .date-group-header{color:#1a2744;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .detail-header,[data-theme="light"] .profile-header,[data-theme="light"] .donors-section,[data-theme="light"] .bt-detail-header{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .acct-card{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .news-tab.active{color:#1a2744;border-color:#1a2744;}
[data-theme="light"] .story-source{color:#6b7280;}
[data-theme="light"] .profile-sidebar{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .home-title{color:#1a2744;}
[data-theme="light"] .cf-amount,[data-theme="light"] .cf-total{color:#1a2744;}
[data-theme="light"] .chamber-btn.active{background:rgba(26,39,68,0.1);color:#1a2744;border-color:rgba(26,39,68,0.3);}
[data-theme="light"] .swing-chip{background:rgba(26,39,68,0.1);color:#1a2744;}
[data-theme="light"] .score-cell.pass{background:rgba(37,107,66,0.12);}
[data-theme="light"] .score-cell.fail{background:rgba(176,48,32,0.08);}
[data-theme="light"] .map-tooltip-district{color:#1a2744;}
[data-theme="light"] .watchlist-btn-small,[data-theme="light"] .watch-btn{border-color:rgba(160,120,48,0.4);color:#a07830;}
[data-theme="light"] .bt-bill-item{border-bottom-color:rgba(26,39,68,0.08);}
[data-theme="light"] .bt-bill-item:hover{background:#e8ecf4;}
[data-theme="light"] .bt-bill-item.selected{background:#e8ecf4;border-left-color:#1a2744;}
[data-theme="light"] .bt-bill-title{color:#1a1e2e;}
[data-theme="light"] .bt-bill-meta{color:#6b7280;}
[data-theme="light"] .bt-left-title{color:#1a1e2e;}
[data-theme="light"] .bt-left-count{color:#6b7280;}
[data-theme="light"] .bt-detail-title{color:#1a1e2e;}
[data-theme="light"] .watch-btn.watching{background:rgba(26,39,68,0.08);border-color:rgba(26,39,68,0.2);color:#1a2744;}
[data-theme="light"] .subject-tag{background:#e8ecf4;border-color:rgba(26,39,68,0.12);color:#1a1e2e;}
[data-theme="light"] .vote-history-btn{background:rgba(160,120,48,0.08);border-color:rgba(160,120,48,0.35);color:#a07830;}
[data-theme="light"] .masthead-badge{background:rgba(26,39,68,0.08);border-color:rgba(26,39,68,0.2);}
[data-theme="light"] .masthead-badge-dot{background:#1a2744;}
[data-theme="light"] .mast-btn.gold{border-color:rgba(160,120,48,0.4);color:#a07830;}
[data-theme="light"] .mast-btn.active{background:rgba(26,39,68,0.1);border-color:rgba(26,39,68,0.3);color:#1a2744;}
[data-theme="light"] .issue-tag{background:#e8ecf4;border-color:rgba(26,39,68,0.12);color:#1a1e2e;}
[data-theme="light"] .qp-btn{background:#f0f2f8;border-color:rgba(26,39,68,0.12);color:#6b7280;}
[data-theme="light"] .empty-card{background:#f0f2f8;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .donor-bar-fill{background:linear-gradient(90deg,#1a2744,#2d4a80);}
[data-theme="light"] .cf-leg-item.active,[data-theme="light"] .bt-bill-item.selected{border-left-color:#1a2744;}
[data-theme="light"] .subject-tag{background:#e8ecf4;border-color:rgba(26,39,68,0.12);color:#1a1e2e;}
[data-theme="light"] .plan-badge,[data-theme="light"] .admin{color:#1a2744;}
[data-theme="light"] .login-card{background:#f8f9fc;border-color:rgba(26,39,68,0.12);box-shadow:0 4px 24px rgba(26,39,68,0.1);}
`;

/* ─── CO-SPONSOR DROPDOWN ─────────────────────────────────── */
function CoSponsorDropdown({ coSponsors }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          background:"var(--surface2)", border:"1px solid var(--border)", borderRadius:"5px",
          padding:"0.25rem 0.65rem", color:"var(--gold)", fontFamily:"var(--font-mono)",
          fontSize:"0.62rem", cursor:"pointer", letterSpacing:"0.06em", transition:"all 0.18s",
          marginTop:"0.25rem",
        }}
      >
        {open ? "▲ Hide" : "▼ +" + coSponsors.length + " more co-sponsors"}
      </button>
      {open && (
        <div style={{ marginTop:"0.35rem", borderLeft:"2px solid var(--border)", paddingLeft:"0.75rem" }}>
          {coSponsors.map(s => (
            <div key={s.people_id} className="sponsor-item">{s.name} ({s.party})</div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── AI BILL DESCRIPTION ─────────────────────────────────── */
function AIBillDescription({ bill, session }) {
  const authHeader = session?.access_token ? { "Authorization": `Bearer ${session.access_token}` } : {};
  const [aiDesc, setAiDesc] = useState(null);
  const [loading, setLoading] = useState(false);
  const PROXY = process.env.REACT_APP_PROXY_URL || "https://legisim-server.onrender.com";

  const generate = async () => {
    if (loading || aiDesc) return;
    setLoading(true);
    try {
      const res = await fetch(PROXY + "/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json",
          ...authHeader },
        body: JSON.stringify({
          model: "claude-haiku-4-5-20251001",
          max_tokens: 200,
          system: "You are a Michigan legislative analyst. Explain bills in plain English for lobbyists and citizens. Be concise — 2-3 sentences max. No legal jargon.",
          messages: [{
            role: "user",
            content: "Explain this Michigan bill in plain English: " + bill.bill_number + " — " + bill.title + (bill.description ? ". Official description: " + bill.description : "")
          }]
        })
      });
      const data = await res.json();
      const text = data.content?.map(c => c.text || "").join("") || "";
      setAiDesc(text);
    } catch(e) {}
    setLoading(false);
  };

  return (
    <div style={{ padding:"0.25rem 1.5rem 0.75rem" }}>
      <div style={{ display:"flex", alignItems:"center", gap:"0.75rem", marginBottom:"0.4rem" }}>
        <div className="section-label" style={{ padding:0, borderTop:"none", marginBottom:0 }}>AI PLAIN-ENGLISH SUMMARY</div>
        {!aiDesc && !loading && (
          <button
            onClick={generate}
            style={{ background:"var(--gold-glow)", border:"1px solid var(--gold-dim)", borderRadius:"5px", padding:"0.18rem 0.55rem", color:"var(--gold)", fontFamily:"var(--font-mono)", fontSize:"0.58rem", cursor:"pointer", letterSpacing:"0.06em" }}
          >Generate ✦</button>
        )}
      </div>
      {loading && (
        <div style={{ fontFamily:"var(--font-mono)", fontSize:"0.68rem", color:"var(--muted)" }}>
          Generating summary…
        </div>
      )}
      {aiDesc && (
        <div style={{ fontSize:"0.83rem", color:"var(--text)", lineHeight:1.65, fontWeight:300 }}>
          {aiDesc}
        </div>
      )}
      {!aiDesc && !loading && (
        <div style={{ fontFamily:"var(--font-mono)", fontSize:"0.65rem", color:"var(--muted)", fontStyle:"italic" }}>
          Click Generate for an AI plain-English explanation of this bill.
        </div>
      )}
    </div>
  );
}

/* ─── BILL DETAIL PANEL ───────────────────────────────────── */
function BillDetail({ item, onWatch, isWatched, onNavigate, watchCount, clients = [], userId, toast , session }) {
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAssign, setShowAssign] = useState(false);
  const [assignSelected, setAssignSelected] = useState(new Set());
  const [assignSaving, setAssignSaving] = useState(false);

  useEffect(() => {
    if (!item) return;
    setBill(null);
    setError(null);

    const cached = getBillCache(item.bill_id);
    if (cached && cached.change_hash === item.change_hash) {
      setBill(cached.bill);
      return;
    }

    setLoading(true);
    fetchBill(item.bill_id)
      .then(b => {
        setBillCache(item.bill_id, b, item.change_hash);
        setBill(b);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [item?.bill_id, item?.change_hash]);

  if (!item) return (
    <div className="bt-empty">
      <div className="bt-empty-icon">📜</div>
      <div className="bt-empty-text">Select a bill from the list to view full details and track its status.</div>
    </div>
  );

  if (loading) return (
    <div className="bt-detail-loading">
      <div className="bt-loading-ring" />
      Loading bill details…
    </div>
  );

  if (error) return (
    <div className="bt-error">Failed to load bill details: {error}</div>
  );

  if (!bill) return null;

  const sc = statusStyle(item.status);
  const watching = isWatched(item.bill_id);
  const primarySponsor = bill.sponsors?.find(s => s.sponsor_type_id === 1);
  const coSponsors = bill.sponsors?.filter(s => s.sponsor_type_id !== 1) || [];
  const latestVote = bill.votes?.[0];

  return (
    <>
    <div className="bt-detail">
      <div className="bt-detail-header">
        <div className="bt-detail-number">
          {bill.bill_number} · {bill.state} · {bill.session?.session_name || "2025-2026"}
        </div>
        <div className="bt-detail-title">{bill.title}</div>
        <div className="bt-detail-actions">
          <span className="status-badge" style={{ background:sc.bg, border:"1px solid "+sc.border, color:sc.text, padding:"0.25rem 0.65rem", fontSize:"0.65rem" }}>
            {item.status}
          </span>
          <button className={"watch-btn" + (watching ? " watching" : "")} onClick={() => onWatch(item)}>
            {watching ? "📌 Tracking — Click to Remove" : "＋ Track This Bill"}
          </button>
          <button className="btn-assign-client" onClick={() => { setAssignSelected(new Set()); setShowAssign(true); }}>
            👥 Track for Client
          </button>
          <a
            href={(() => { const m = bill.bill_number.match(/^([A-Z]+)(\d+)$/); const actionYr = parseInt((bill.last_action_date||"2025").slice(0,4)); const yr = actionYr >= 2026 ? actionYr - 1 : actionYr; return m ? `https://www.legislature.mi.gov/Bills/Bill?ObjectName=${yr}-${m[1]}-${m[2]}` : "#"; })()}
            target="_blank"
            rel="noopener noreferrer"
            style={{ display:"inline-flex", alignItems:"center", gap:"0.3rem", background:"var(--surface2)", border:"1px solid var(--border)", borderRadius:"6px", padding:"0.35rem 0.75rem", color:"var(--text)", fontFamily:"var(--font-mono)", fontSize:"0.62rem", textDecoration:"none", cursor:"pointer", letterSpacing:"0.04em", whiteSpace:"nowrap" }}
            onClick={e => e.stopPropagation()}
          >
            Legislature.mi.gov ↗
          </a>
          {watchCount > 0 && (
            <button className="mast-btn gold" style={{ fontSize:"0.62rem" }} onClick={() => onNavigate("trackedlegislation")}>
              View Tracked ({watchCount}) →
            </button>
          )}
        </div>
      </div>

      <div style={{padding:"0 1.75rem 0.5rem"}}>
        <BillStatusBadge status={item.status} lastAction={item.last_action} chamber={item.chamber} lastActionDate={item.last_action_date} />
      </div>

      {bill.description && (
        <>
          <div className="section-label">Description</div>
          <div className="bt-description">{bill.description}</div>
        </>
      )}
      <AIBillDescription bill={bill} session={session} />

      <div className="section-label">Bill Details</div>
      <div className="bt-info-grid">
        <div className="bt-info-card">
          <div className="bt-info-label">Status</div>
          <div className="bt-info-value">{item.status}</div>
        </div>
        <div className="bt-info-card">
          <div className="bt-info-label">Last Action</div>
          <div className="bt-info-value">{bill.history?.[0]?.action || item.last_action || "—"}</div>
        </div>
        <div className="bt-info-card">
          <div className="bt-info-label">Last Action Date</div>
          <div className="bt-info-value">
            {(bill?.last_action_date || item.last_action_date) ? new Date(bill?.last_action_date || item.last_action_date).toLocaleDateString("en-US", { month:"long", day:"numeric", year:"numeric" }) : "—"}
          </div>
        </div>
        <div className="bt-info-card">
          <div className="bt-info-label">Chamber</div>
          <div className="bt-info-value">{item.chamber}</div>
        </div>
        {bill.committee?.committee_name && (
          <div className="bt-info-card" style={{ gridColumn:"span 2" }}>
            <div className="bt-info-label">Committee</div>
            <div className="bt-info-value">{bill.committee.committee_name}</div>
          </div>
        )}
      </div>

      {primarySponsor && (
        <>
          <div className="section-label">Sponsor{coSponsors.length > 0 ? "s" : ""}</div>
          <div className="sponsor-list">
            <div className="sponsor-item" style={{ color:"var(--gold)", fontWeight:500 }}>
              {primarySponsor.name} ({primarySponsor.party}) — Primary Sponsor
            </div>
            {coSponsors.slice(0, 5).map(s => (
              <div key={s.people_id} className="sponsor-item">{s.name} ({s.party})</div>
            ))}
            {coSponsors.length > 5 && (
              <CoSponsorDropdown coSponsors={coSponsors.slice(5)} />
            )}
          </div>
        </>
      )}

      {latestVote && (
        <>
          <div className="section-label">Latest Vote</div>
          <div className="vote-row">
            <div className="vote-chip">
              <div className="vote-chip-val yea">{latestVote.yea}</div>
              <div className="vote-chip-label">Yea</div>
            </div>
            <div className="vote-chip">
              <div className="vote-chip-val nay">{latestVote.nay}</div>
              <div className="vote-chip-label">Nay</div>
            </div>
            {latestVote.absent > 0 && (
              <div className="vote-chip">
                <div className="vote-chip-val absent">{latestVote.absent}</div>
                <div className="vote-chip-label">Absent</div>
              </div>
            )}
            <div className="vote-chip" style={{ flex:1 }}>
              <div className="vote-chip-val" style={{ fontSize:"0.8rem" }}>{latestVote.desc || "Floor Vote"}</div>
              <div className="vote-chip-label">{latestVote.date}</div>
            </div>
          </div>
        </>
      )}

      {bill.subjects?.length > 0 && (
        <>
          <div className="section-label">Subject Areas</div>
          <div className="subject-tags">
            {bill.subjects.map(s => <span key={s.subject_id} className="subject-tag">{s.subject_name}</span>)}
          </div>
        </>
      )}

      <div className="bt-source">
        Data provided by LegiScan (legiscan.com) · CC BY 4.0 · Updated weekly
      </div>
    </div>

    {/* ASSIGN TO CLIENT MODAL */}
    {showAssign && (
      <div className="bt-assign-overlay" onClick={() => setShowAssign(false)}>
        <div className="bt-assign-modal" onClick={e => e.stopPropagation()}>
          <div className="bt-assign-head">
            <div>
              <div className="bt-assign-title">Track Bill for Client</div>
              <div className="bt-assign-sub">{item.bill_number} — {item.title?.slice(0,60)}{item.title?.length > 60 ? "…" : ""}</div>
            </div>
            <button className="bt-assign-cancel" onClick={() => setShowAssign(false)}>✕</button>
          </div>
          <div className="bt-assign-body">
            {clients.length === 0 ? (
              <div className="bt-assign-empty">No clients found. Add clients in the Client Services page first.</div>
            ) : clients.map(c => (
              <div
                key={c.id}
                className={`bt-assign-row${assignSelected.has(c.id) ? " sel" : ""}`}
                onClick={() => setAssignSelected(prev => { const s = new Set(prev); s.has(c.id) ? s.delete(c.id) : s.add(c.id); return s; })}
              >
                <div className="bt-assign-check">{assignSelected.has(c.id) ? "✓" : ""}</div>
                <div>
                  <div className="bt-assign-name">{c.name}</div>
                  {c.organization && <div className="bt-assign-org">{c.organization}</div>}
                </div>
              </div>
            ))}
          </div>
          <div className="bt-assign-foot">
            <button className="bt-assign-cancel" onClick={() => setShowAssign(false)}>Cancel</button>
            <button
              className="bt-assign-save"
              disabled={assignSelected.size === 0 || assignSaving}
              onClick={async () => {
                if (!userId || assignSelected.size === 0) return;
                setAssignSaving(true);
                for (const clientId of assignSelected) {
                  await supabase.from("client_bills").upsert({
                    client_id: clientId,
                    bill_id: item.bill_id,
                    bill_number: item.bill_number,
                    title: item.title,
                    status: item.status,
                    last_action: item.last_action,
                    last_action_date: item.last_action_date,
                    added_at: new Date().toISOString(),
                  }, { onConflict: "client_id,bill_id" });
                }
                setAssignSaving(false);
                setShowAssign(false);
                setAssignSelected(new Set());
                const count = assignSelected.size;
                if (toast) toast(`Bill assigned to ${count} client${count > 1 ? "s" : ""} ✓`);
              }}
            >
              {assignSaving ? "Saving…" : `Add to ${assignSelected.size} Client${assignSelected.size !== 1 ? "s" : ""}`}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

/* ─── MAIN PAGE ───────────────────────────────────────────── */
async function handleSignOut(supabaseClient, onNavigate) {
  await supabaseClient.auth.signOut();
  onNavigate('home');
}

export default function BillTrackerPage({ onNavigate, session }) {
  const authHeader = session?.access_token ? { "Authorization": `Bearer ${session.access_token}` } : {};
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [chamberFilter, setChamberFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [selected, setSelected] = useState(null);
  const [watchedMap, setWatchedMap] = useState({});
  const [watchToast, setWatchToast] = useState(null);
  const { toasts, toast, dismiss } = useToast();
  const [searchTimer, setSearchTimer] = useState(null);
  const [clients, setClients] = useState([]);
  const [confirmState, setConfirmState] = useState(null);
  const confirm = (message, subtext, onConfirm) => setConfirmState({ message, subtext, onConfirm });

  const userId = session?.user?.id;

  // Load clients for assign-to-client feature
  useEffect(() => {
    if (!userId) return;
    supabase.from("clients").select("id,name,organization").eq("user_id", userId).order("name")
      .then(({ data }) => { if (data) setClients(data); });
  }, [userId]);

  // Load watchlist from Supabase on mount
  useEffect(() => {
    if (!userId) return;
    getWatchlist(userId).then(list => {
      const map = {};
      list.forEach(b => { map[b.bill_id] = true; });
      setWatchedMap(map);
    });
  }, [userId]);

  // On mount check if we should auto-select a specific bill (from Tracked Legislation "View in Search")
  useEffect(() => {
    const openBillId = sessionStorage.getItem("wc_open_bill_id");
    if (openBillId && bills.length > 0) {
      const match = bills.find(b => String(b.bill_id) === openBillId);
      if (match) {
        setSelected(match);
        sessionStorage.removeItem("wc_open_bill_id");
      }
    }
  }, [bills]);

  // On mount check if we should pre-fill search (from Dashboard bill modal)
  useEffect(() => {
    const q = sessionStorage.getItem("wc_search_query");
    if (q) {
      sessionStorage.removeItem("wc_search_query");
      setSearch(q);
      // Trigger search after a short delay to let component settle
      setTimeout(() => {
        const el = document.querySelector(".bt-search-input");
        if (el) { el.value = q; el.dispatchEvent(new Event("input", { bubbles: true })); }
      }, 100);
    }
  }, []);

  // Load master list on mount (cached 1h, busted if server has more bills)
  useEffect(() => {
    const cached = getMasterListCache();
    const cachedArr = cached ? (Array.isArray(cached) ? cached : Object.values(cached)) : null;
    const isNewFormat = cachedArr && cachedArr.length > 0 && cachedArr[0].bill_number && cachedArr[0].description !== undefined;

    if (isNewFormat) {
      // Show cached data immediately while checking server for fresher data
      const dedup1 = arr => { const seen=new Set(); return arr.filter(b => b.bill_id && !seen.has(b.bill_id) && seen.add(b.bill_id)); };
      setBills(dedup1(cachedArr.map(masterlistToItem).filter(b => b.bill_number)));
      setLoading(false);
      // Still fetch in background to check if server has more bills
      fetchMasterList().then(masterlist => {
        const arr = Array.isArray(masterlist) ? masterlist : Object.values(masterlist);
        // Refresh if server has more bills OR any bill has a newer last_action_date
        const newestCached = cachedArr.reduce((max, b) => b.last_action_date > max ? b.last_action_date : max, "");
        const newestServer = arr.reduce((max, b) => b.last_action_date > max ? b.last_action_date : max, "");
        if (arr.length > cachedArr.length || newestServer > newestCached) {
          setMasterListCache(masterlist);
          const dedup2 = a => { const seen=new Set(); return a.filter(b => b.bill_id && !seen.has(b.bill_id) && seen.add(b.bill_id)); };
          setBills(dedup2(arr.map(masterlistToItem).filter(b => b.bill_number)));
        }
      }).catch(() => {}); // silent fail — already showing cached data
      return;
    }

    // No valid cache — clear any stale data and fetch fresh
    localStorage.removeItem(CACHE_KEY_MASTERLIST);
    localStorage.removeItem(CACHE_KEY_MASTERLIST_TS);
    fetchMasterList()
      .then(masterlist => {
        setMasterListCache(masterlist);
        const arr = Array.isArray(masterlist) ? masterlist : Object.values(masterlist);
        const dedup3 = a => { const seen=new Set(); return a.filter(b => b.bill_id && !seen.has(b.bill_id) && seen.add(b.bill_id)); };
        setBills(dedup3(arr.map(masterlistToItem).filter(b => b.bill_number)));
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  // Debounced search
  useEffect(() => {
    if (searchTimer) clearTimeout(searchTimer);
    if (!search.trim()) { setSearchResults(null); return; }

    // PA search: any variation of "PA 25", "PA25", "pa25", "pa-25", "Pa 25", "public act 25"
    const normalized = search.trim().replace(/[\s\-_]+/g, "").toLowerCase();
    const paMatch = normalized.match(/^pa(\d+)$/) || search.trim().match(/^public\s*act\s*(\d+)$/i);
    if (paMatch) {
      const paNum = paMatch[1];
      const paResults = bills.filter(b => {
        const action = (b.last_action || "").toLowerCase();
        const isEnrolled = b.status === "Enrolled" || b.status === 3 || b.status_text === "Enrolled";
        if (!isEnrolled) return false;
        // match on action text or just return all enrolled if no specific number
        return paNum === "0" || 
          action.includes("public act " + paNum) ||
          action.includes("public act no. " + paNum) ||
          action.includes("pa " + paNum) ||
          action.includes("pa" + paNum) ||
          action.includes("act " + paNum);
      });
      // If no specific matches found, return all enrolled bills
      setSearchResults(paResults.length > 0 ? paResults : bills.filter(b => 
        b.status === "Enrolled" || b.status === 3 || b.status_text === "Enrolled"
      ));
      return;
    }

    const timer = setTimeout(() => {
      setSearching(true);
      fetchSearch(search.trim())
        .then(results => {
          const items = Object.values(results)
            .filter(r => r.bill_id)
            .map(r => ({
              bill_id:     r.bill_id,
              bill_number: r.bill_number,
              title:       r.title,
              status:      statusLabel(r.status),
              last_action: r.last_action,
              last_action_date: r.last_action_date,
              change_hash: r.change_hash,
              chamber:     r.bill_number?.startsWith("H") ? "House" : "Senate",
            }));
          setSearchResults(items);
          setSearching(false);
        })
        .catch(() => { setSearchResults([]); setSearching(false); });
    }, 500);
    setSearchTimer(timer);
  }, [search]);

  const handleWatch = async (item) => {
    if (!userId) {
      toast("Sign in to track bills", "warning");
      return;
    }
    if (watchedMap[item.bill_id]) {
      confirm(`Remove ${item.bill_number}?`, "It will be removed from your tracked bills.", async () => {
        await removeFromWatchlist(userId, item.bill_id);
        setWatchedMap(m => { const n = {...m}; delete n[item.bill_id]; return n; });
        toast(`${item.bill_number} removed from tracked bills`, "info");
        setConfirmState(null);
      });
    } else {
      await addToWatchlist(userId, item);
      setWatchedMap(m => ({ ...m, [item.bill_id]: true }));
      toast(`${item.bill_number} tracked ✓`);
    }
  };

  const baseList = searchResults !== null ? searchResults : bills;
  const ACTIVE_STATUSES  = new Set(["Introduced","In Progress","In Committee","Draft"]);
  const PASSED_STATUSES  = new Set(["Passed"]);
  const PA_STATUSES      = new Set(["PA Signed"]);

  const displayList = baseList.filter(b => {
    const matchChamber = chamberFilter === "All" || b.chamber === chamberFilter;
    const matchStatus =
      statusFilter === "All"      ? true :
      statusFilter === "Active"   ? ACTIVE_STATUSES.has(b.status) :
      statusFilter === "Passed"   ? PASSED_STATUSES.has(b.status) :
      statusFilter === "PA"       ? PA_STATUSES.has(b.status) :
      statusFilter === "Failed"   ? (b.status === "Failed" || b.status === "Vetoed") :
      false;
    return matchChamber && matchStatus;
  });

  const watchCount = Object.keys(watchedMap).length;

  return (
    <div className="bt-app">
      <style>{CSS}</style>
      <MobileMenuCSS />
      <div className="legisim-masthead">
        <div className="masthead-left">
          <div className="masthead-wordmark">Whip<em>Count</em><sup>TM</sup></div>
          <button className="mast-back" onClick={() => onNavigate("dashboard")}>← Dashboard</button>
        </div>
        <div className="masthead-right">
          <button className="mast-btn-sm" onClick={() => onNavigate("account")}>Account</button>
          <button className="mast-btn-sm signout" onClick={() => handleSignOut(supabase, onNavigate)}>Sign Out</button>
        </div>
        </div>
        <div className="bt-body">
        <div className="bt-left">
          <div className="bt-left-header">
            <div className="bt-left-title">Michigan Bills · 2025–2026 Session</div>
            <div className="search-wrap">
              <svg className="search-icon" viewBox="0 0 24 24">
                <circle cx="11" cy="11" r="7" /><line x1="16.5" y1="16.5" x2="22" y2="22" />
              </svg>
              <input className="search-input"
                placeholder="Search bills, keywords, sponsors…"
                value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            {!search && (
              <div className="bt-filters">
                <div style={{display:"flex",gap:"6px",flexWrap:"wrap",marginBottom:"5px"}}>
                  {[["All","All"],["House","House"],["Senate","Senate"]].map(([f,label]) => (
                    <button key={f} className={"bt-filter-btn" + (chamberFilter === f ? " active" : "")}
                      onClick={() => { setChamberFilter(f); setSearchResults(null); setSearch(""); }}>{label}</button>
                  ))}
                </div>
                <div style={{display:"flex",gap:"6px",flexWrap:"wrap"}}>
                  {[
                    ["All","All"],
                    ["Active","Active"],
                    ["Passed","Passed"],
                    ["PA","PA Signed"],
                    ["Failed","Failed / Vetoed"],
                  ].map(([f,label]) => (
                    <button key={f} className={"bt-filter-btn" + (statusFilter === f ? " active" : "")}
                      onClick={() => { setStatusFilter(f); setSearchResults(null); setSearch(""); }}>{label}</button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="bt-list-meta">
            <span>
              {searching ? "Searching…" : `${displayList.length} bill${displayList.length !== 1 ? "s" : ""}${search ? ' matching "' + search + '"' : ""}`}
            </span>
            <button className="watchlist-btn-small" onClick={() => onNavigate("trackedlegislation")}>
              📌 Tracked ({watchCount})
            </button>
          </div>

          {loading ? (
            <BillListSkeleton count={10} />
          ) : error ? (
            <div className="bt-error">Failed to load bills: {error}</div>
          ) : (
            <div className="bt-bill-list">
              {displayList.map(bill => {
                const sc = statusStyle(bill.status);
                return (
                  <div key={bill.bill_id}
                    className={"bt-bill-item" + (selected?.bill_id === bill.bill_id ? " selected" : "")}
                    onClick={() => setSelected(bill)}
                  >
                    <div className="bt-bill-number" style={{display:"flex",alignItems:"center",gap:"0.5rem"}}>
                      {bill.bill_number}
                      <a
                        href={(() => { const m = bill.bill_number.match(/^([A-Z]+)(\d+)$/); const actionYr = parseInt((bill.last_action_date||"2025").slice(0,4)); const yr = actionYr >= 2026 ? actionYr - 1 : actionYr; return m ? `https://www.legislature.mi.gov/Bills/Bill?ObjectName=${yr}-${m[1]}-${m[2]}` : "#"; })()}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color:"var(--gold)", fontSize:"0.7rem", textDecoration:"none", fontWeight:700, lineHeight:1 }}
                        onClick={e => e.stopPropagation()}
                        title="View on Legislature.mi.gov"
                      >↗</a>
                    </div>
                    <div className="bt-bill-title">{bill.title}</div>
                    {bill.description && (
                      <div style={{ fontSize:"0.72rem", color:"var(--muted)", lineHeight:1.4, marginBottom:"0.3rem", fontWeight:300 }}>
                        {bill.description.length > 100 ? bill.description.slice(0, 100) + "…" : bill.description}
                      </div>
                    )}
                    <div className="bt-bill-meta">
                      <span className="status-badge" style={{ background:sc.bg, border:"1px solid "+sc.border, color:sc.text }}>
                        {bill.status}
                      </span>
                      {(bill.last_action_date || getBillCache(bill.bill_id)?.bill?.last_action_date) && (
                        <span style={{ fontFamily:"var(--font-mono)", fontSize:"0.58rem", color:"var(--muted)" }}>
                          {new Date(bill.last_action_date || getBillCache(bill.bill_id)?.bill?.last_action_date).toLocaleDateString("en-US", { month:"short", day:"numeric", year:"numeric" })}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
              {displayList.length === 0 && !loading && (
                <div className="bt-empty">
                  <div className="bt-empty-icon">🔍</div>
                  <div className="bt-empty-text">No bills match your search.<br/>Try different keywords or clear your filters.</div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="bt-main">
          <BillDetail
            item={selected}
            onWatch={handleWatch}
            isWatched={bill_id => watchedMap[bill_id]}
            onNavigate={onNavigate}
            watchCount={watchCount}
            clients={clients}
            userId={userId}
            toast={toast}
           session={session} />
        </div>
      </div>

      {confirmState && <ConfirmDialog message={confirmState.message} subtext={confirmState.subtext} onConfirm={confirmState.onConfirm} onCancel={() => setConfirmState(null)} />}
      <ToastContainer toasts={toasts} dismiss={dismiss} />
    </div>
  );
}
